import { buildCloseTaskUpdateData } from "@coworker/app/src/hooks/TaskActions/useCloseTask";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import {
  BulkUpdateTaskV1Item,
  BulkUpdateTaskV2,
} from "@coworker/apprestructured/src/task-management/services/tasks.service";
import {
  useUpdateTasksV1,
  useUpdateTasksV2,
} from "@coworker/apprestructured/src/task-management/services/useUpdateTasks";
import { TaskListItem } from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { closeTypeOptions } from "@coworker/enums/closeTypes";
import { useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";

const V2_CLOSE_REASON_ID_TO_V1_CLOSE_TYPE: Record<string, string> = {
  1: closeTypeOptions.INCORRECT,
  2: closeTypeOptions.ALREADY_RESOLVED,
  3: closeTypeOptions.NOT_IN_STOCK,
  4: closeTypeOptions.OTHER,
  5: closeTypeOptions.OTHER,
};

type BasicBulkUpdateTasksPayload = {
  closeReasonId: string;
  description: string;
};

const updateTasksV1Payload = (
  tasks: TaskListItem[],
  {
    closeReasonId,
    description,
    currentUserUid,
    storeId,
  }: BasicBulkUpdateTasksPayload & { currentUserUid: string; storeId: string }
) => {
  const tasksV1 = tasks.filter((task) => task.type === "v1");
  const close_type =
    V2_CLOSE_REASON_ID_TO_V1_CLOSE_TYPE[closeReasonId] ??
    closeTypeOptions.OTHER;
  return tasksV1.map<BulkUpdateTaskV1Item>((task) => ({
    task_id: task.task_data.id,
    patch: buildCloseTaskUpdateData({
      task: task.task_data,
      uid: currentUserUid,
      storeId,
      close_type: close_type,
      close_description: description,
    }),
  }));
};

const getTasksV2Payload = (
  tasks: TaskListItem[],
  { closeReasonId, description }: BasicBulkUpdateTasksPayload
) => {
  const tasksV2 = tasks.filter((task) => task.type === "v2");
  // The backend replaces `details` entirely rather than deep merging,
  // so we always send the full details object merged with any pending
  // changes. This also covers pick-up, where details must be present
  // even if the user hasn't changed anything.
  return tasksV2.map<BulkUpdateTaskV2>((task) => ({
    id: task.task_data.id,
    patch: {
      ...task.task_data,
      state: "closed",
      close_reason_id: closeReasonId,
      close_description: description,
    },
  }));
};

export type CloseTaskPayload = {
  tasks: TaskListItem[];
  closeReasonId: string | null;
  description: string;
  storeId: string;
  currentUserUid: string;
};

export const useCloseTask = () => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();

  const { mutateAsync: updateTasksV2 } = useUpdateTasksV2();
  const { mutateAsync: updateTasksV1 } = useUpdateTasksV1();
  const queryClient = useQueryClient();

  const closeTask = async ({
    tasks,
    closeReasonId,
    description,
    storeId,
    currentUserUid,
  }: CloseTaskPayload) => {
    try {
      const tasksV1Payload = updateTasksV1Payload(tasks, {
        closeReasonId: closeReasonId!,
        description,
        currentUserUid,
        storeId,
      });
      const updateTaskV1Promise = tasksV1Payload.length
        ? updateTasksV1(tasksV1Payload)
        : Promise.resolve();

      const tasksV2Payload = getTasksV2Payload(tasks, {
        closeReasonId: closeReasonId!,
        description,
      });
      const updateTasksV2Promise = tasksV2Payload.length
        ? updateTasksV2({ tasks: tasksV2Payload })
        : Promise.resolve();

      await Promise.all([updateTaskV1Promise, updateTasksV2Promise]);
      await invalidateTaskQueries(queryClient);

      showToast(t("closedNTasksString", { count: tasks.length }));
    } catch (error) {
      showToast(t("failedToCloseAndDeleteTeamTasksString"));
      throw error;
    }
  };

  return { closeTask };
};
